import React, { useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Card, CardBody, Listbox, ListboxItem } from "@heroui/react";
import { Icon } from "@iconify/react";
import { useTranslation } from "react-i18next";
import { useUser } from "@webuild-ai/react-auth";

import { PageTitle } from "@/components/UI/PageTitle";

import { AppearanceSettings } from "./AppearanceSettings";
import { ExportSettings } from "./ExportSettings";
import { TemplateSettings } from "./TemplateSettings";
import { IntegrationsSettings } from "./IntegrationsSettings";
import { PromptShortcutsSettings } from "./PromptShortcutsSettings";
import { AboutSettings } from "./AboutSettings";

/**
 * Tab key → hash mapping for deep linking.
 * Keeps URL in sync with active tab (e.g. /settings#integrations).
 */
const SETTINGS_TABS = [
  {
    key: "general",
    icon: "solar:pallete-2-outline",
    titleKey: "General",
  },
  {
    key: "integrations",
    icon: "solar:plug-circle-outline",
    titleKey: "Integrations",
  },
  {
    key: "exports",
    icon: "solar:export-outline",
    titleKey: "Exports",
  },
  {
    key: "shortcuts",
    icon: "solar:command-outline",
    titleKey: "Shortcuts",
  },
  {
    key: "about",
    icon: "solar:info-circle-outline",
    titleKey: "About",
  },
] as const;

type SettingsTabKey = (typeof SETTINGS_TABS)[number]["key"];

const DEFAULT_TAB: SettingsTabKey = "general";

export const SettingsPage: React.FC = () => {
  const { userProfile } = useUser();
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  /** Derive active tab from URL hash, falling back to default. */
  const activeTab: SettingsTabKey = useMemo(() => {
    const hash = location.hash.substring(1) as SettingsTabKey;
    const valid = SETTINGS_TABS.some((tab) => tab.key === hash);

    return valid ? hash : DEFAULT_TAB;
  }, [location.hash]);

  /** Sync hash on tab change. */
  const handleTabChange = (keys: "all" | Set<React.Key>) => {
    const key = Array.from(keys)[0] as SettingsTabKey;

    if (key) {
      navigate({ hash: key === DEFAULT_TAB ? "" : key }, { replace: true });
    }
  };

  /** Handle legacy hash anchors (e.g. #appearance → #general). */
  useEffect(() => {
    const hash = location.hash.substring(1);
    const legacyMap: Record<string, SettingsTabKey> = {
      appearance: "general",
      templates: "exports",
      "export-settings": "exports",
    };

    if (legacyMap[hash]) {
      navigate({ hash: legacyMap[hash] }, { replace: true });
    }
  }, [location.hash, navigate]);

  if (!userProfile) {
    throw new Error("User profile not found");
  }

  return (
    <div className="flex flex-col h-full w-full overflow-hidden">
      {/* Header */}
      <div className="shrink-0 p-4 pb-2">
        <PageTitle
          centered
          dataId="settings-title"
          icon="solar:settings-outline"
          subtitle="Settings Description"
          title="Settings"
        />
      </div>

      {/* Content: sidebar nav + panel in a single card */}
      <div className="flex-1 min-h-0 px-4 pb-4 overflow-hidden">
        <Card className="h-full" shadow="sm">
          <CardBody className="p-0 flex flex-col md:flex-row overflow-hidden">
            {/* Sidebar navigation */}
            <nav className="shrink-0 md:w-52 border-b md:border-b-0 md:border-r border-default-200 p-2">
              <Listbox
                aria-label={t("Settings tabs")}
                selectedKeys={new Set([activeTab])}
                selectionMode="single"
                onSelectionChange={handleTabChange}
              >
                {SETTINGS_TABS.map((tab) => (
                  <ListboxItem
                    key={tab.key}
                    classNames={{
                      base: "data-[selected=true]:bg-primary/10 data-[selected=true]:text-primary rounded-lg px-3 py-2.5",
                      selectedIcon: "hidden",
                    }}
                    startContent={<Icon icon={tab.icon} width={18} />}
                    textValue={t(tab.titleKey)}
                  >
                    <span className="text-sm font-medium">{t(tab.titleKey)}</span>
                  </ListboxItem>
                ))}
              </Listbox>
            </nav>

            {/* Panel content */}
            <div className="flex-1 min-h-0 overflow-y-auto p-6">
              <div className="space-y-8">
                <SettingsTabContent tabKey={activeTab} />
              </div>
            </div>
          </CardBody>
        </Card>
      </div>
    </div>
  );
};

/** Renders the content for each settings tab. */
const SettingsTabContent: React.FC<{ tabKey: SettingsTabKey }> = ({ tabKey }) => {
  switch (tabKey) {
    case "general":
      return <AppearanceSettings />;
    case "integrations":
      return <IntegrationsSettings />;
    case "exports":
      return (
        <>
          <ExportSettings />
          <TemplateSettings />
        </>
      );
    case "shortcuts":
      return <PromptShortcutsSettings />;
    case "about":
      return <AboutSettings />;
  }
};
